import { useCallback, useEffect, useRef } from 'react'
import { z } from 'zod'

import Env from '@/env/env'
import getJWTToken from '@/utils/getJWTToken'

export const userStatusSchema = z.enum([
  'active',
  'pending_on_boarding',
  'pending_email_verification',
])
export const sessionSchema = z.object({
  token: z.string().min(1),
  userStatus: userStatusSchema,
  mfaRequired: z.literal(false).optional(),
})
export type FinalSession = z.infer<typeof sessionSchema>
export const challengeSchema = z.object({
  mfaRequired: z.literal(true),
  challengeToken: z.string().min(1),
  expiresIn: z.number().positive().max(300),
  methods: z.array(z.enum(['totp', 'recovery_code'])).min(1),
  token: z.never().optional(),
})
export type MfaChallenge = z.infer<typeof challengeSchema>
// Check the discriminator before parsing: never strip mfaRequired and accept
// a malformed hybrid response as a completed session.
export const loginResultSchema = z.union([
  challengeSchema,
  z
    .object({ mfaRequired: z.literal(false).optional() })
    .passthrough()
    .and(sessionSchema),
])
export const securityStatusSchema = z
  .object({
    mfaAvailable: z.boolean(),
    mfaEnabled: z.boolean(),
    method: z.enum(['totp']).nullable(),
    recoveryCodesRemaining: z.number().int().nonnegative(),
    emailVerified: z.boolean(),
    passwordManagement: z.enum(['wecheck', 'microsoft', 'mixed', 'unknown']),
    canChangePassword: z.boolean(),
  })
  .refine((value) => value.mfaEnabled === (value.method === 'totp'))
export type SecurityStatus = z.infer<typeof securityStatusSchema>
export type FactorMethod = 'totp' | 'recovery_code'
export const actionSchema = z.enum([
  'enroll_mfa',
  'disable_mfa',
  'rotate_recovery_codes',
])
export type SecurityAction = z.infer<typeof actionSchema>
export const proofSchema = z.object({
  reauthToken: z.string().min(1),
  expiresIn: z.number().positive().max(300),
  action: actionSchema,
})
export const microsoftProofSchema = proofSchema
export const enrollmentSchema = z.object({
  enrollmentToken: z.string().min(1),
  otpauthUri: z.string().startsWith('otpauth://totp/'),
  manualKey: z.string().regex(/^[A-Z2-7]+=*$/i),
  expiresIn: z.number().positive().max(600),
})
export type Enrollment = z.infer<typeof enrollmentSchema>
export const enabledSchema = z
  .object({
    mfaEnabled: z.literal(true),
    recoveryCodes: z.array(z.string().min(1)).min(1),
  })
  .and(sessionSchema)
export const disabledSchema = z
  .object({ mfaEnabled: z.literal(false) })
  .and(sessionSchema)
export const passwordChangedSchema = z.object({
  signInRequired: z.literal(true),
})
export const emptyResponseSchema = z.undefined()

const knownCodes = new Set([
  'MFA_REQUIRED',
  'INVALID_MFA_CODE',
  'MFA_CODE_ALREADY_USED',
  'MFA_CHALLENGE_EXPIRED',
  'REAUTH_REQUIRED',
  'INVALID_CURRENT_PASSWORD',
  'SECURITY_UNAVAILABLE',
  'MFA_NOT_ENABLED',
  'MFA_ALREADY_ENABLED',
  'TOO_MANY_ATTEMPTS',
  'INVALID_CREDENTIALS',
  'INVALID_TOKEN',
])

// Never retain the request, response body, URL, server message or original
// exception in an error. Those can contain credentials and one-time artifacts.
export class SecurityError extends Error {
  constructor(
    public readonly code: string,
    public readonly status = 0,
  ) {
    super('Authentication request failed')
    this.name = 'SecurityError'
  }
  get uncertain() {
    return (
      this.status === 0 ||
      this.status >= 500 ||
      this.code === 'INVALID_RESPONSE'
    )
  }
}

export type SensitiveOptions = {
  body?: unknown
  authenticated?: boolean
  signal?: AbortSignal
  method?: 'GET' | 'POST'
}

/** A single fetch, no RTK/Redux, no retries, no secret-bearing diagnostics. */
export async function sensitiveRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  options: SensitiveOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.authenticated) {
    const token = getJWTToken()
    if (!token) throw new SecurityError('REAUTH_REQUIRED', 401)
    headers.Authorization = `Bearer ${token}`
  }
  let response: Response
  let payload: any
  const controller = new AbortController()
  const cancel = () => controller.abort()
  if (options.signal?.aborted) controller.abort()
  options.signal?.addEventListener('abort', cancel, { once: true })
  const timeout = setTimeout(cancel, 20000)
  try {
    response = await fetch(`${Env.REACT_APP_BASE_API_URL}${path}`, {
      method: options.method ?? 'POST',
      credentials: 'include',
      cache: 'no-store',
      redirect: 'error',
      headers,
      signal: controller.signal,
      ...(options.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
    })
    try {
      payload = await response.json()
    } catch {
      throw new SecurityError('INVALID_RESPONSE', response.status)
    }
  } catch (error) {
    if (error instanceof SecurityError)
      throw new SecurityError(error.code, error.status)
    throw new SecurityError('NETWORK_ERROR')
  } finally {
    clearTimeout(timeout)
    options.signal?.removeEventListener('abort', cancel)
  }
  if (!response.ok) {
    const rawCode = payload?.info?.code ?? payload?.code
    const code = knownCodes.has(rawCode)
      ? rawCode
      : response.status === 429
        ? 'TOO_MANY_ATTEMPTS'
        : 'REQUEST_FAILED'
    throw new SecurityError(code, response.status)
  }
  if (
    !payload ||
    typeof payload !== 'object' ||
    payload.status !== response.status
  ) {
    throw new SecurityError('INVALID_RESPONSE', response.status)
  }
  const result = schema.safeParse(payload.info)
  if (!result.success)
    throw new SecurityError('INVALID_RESPONSE', response.status)
  if (options.signal?.aborted || controller.signal.aborted)
    throw new SecurityError('REQUEST_CANCELLED')
  return result.data
}

export function useSensitiveRequest() {
  const controller = useRef<AbortController>()
  useEffect(() => {
    controller.current = new AbortController()
    return () => controller.current?.abort()
  }, [])
  return useCallback(
    async <T>(
      path: string,
      schema: z.ZodType<T>,
      options: SensitiveOptions = {},
    ) =>
      sensitiveRequest(path, schema, {
        ...options,
        signal: controller.current?.signal,
      }),
    [],
  )
}

export function finalSession(value: FinalSession): FinalSession {
  // Deliberate allowlist: recovery codes and factor material never reach Redux.
  return { token: value.token, userStatus: value.userStatus }
}

export function securityErrorKey(error: unknown): string {
  if (error instanceof SecurityError && knownCodes.has(error.code))
    return `security.errors.${error.code}`
  return 'security.errors.generic'
}
